import { Component, DestroyRef, OnDestroy, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { CellEditEndingEventArgs, Column, DataMap, FlexGrid } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, Division, DivisionJob, FeatureType, Grid, JobType, ListItem, ModalSize, VpJob } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { DivisionComponent } from './division.component'

@Component({
    selector: 'app-divisions',
    templateUrl: './divisions.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class DivisionsComponent extends BaseComponent implements OnDestroy {
    private destroyRef = inject(DestroyRef)
    faSearch = faSearch

    active = 1

    divisionsGrid: Grid<Division>
    projectAssignmentsGrid: Grid<VpJob>

    // All divisions (archived included), used for display so an already-assigned archived division still renders.
    divisionMap = new DataMap([], 'value', 'display')
    // Non-archived divisions plus a blank option, used while editing.
    divisionEditMap = new DataMap([], 'value', 'display')

    // jobId -> existing DivisionJob mapping, kept in sync as the user edits.
    private jobToDivisionJob = new Map<string, DivisionJob>()

    private refreshHandler = () => this.refresh()

    constructor() {
        super()

        this.divisionsGrid = new Grid<Division>(this.services)
            .setupModalEditing(this.services.data.divisions, 'Division', DivisionComponent, () => new Division(), this, ModalSize.medium)
            .filterBy(item => [item.name, item.jobTypeNames])
        this.divisionsGrid.canEdit = this.canEdit
        // Reload after add/edit/delete so job-type counts (and the modal's checked state) reflect the saved mapping.
        this.divisionsGrid.modified.on(this.refreshHandler)

        this.projectAssignmentsGrid = new Grid<VpJob>(this.services)
            .isReadOnly(false) // Default to false; only the Division column is actually editable.
            .editOnDoubleClick(false)
            .filterBy(item => [item.id, item.name, this.divisionMap.getDisplayValue(item['divisionId'])])
        this.projectAssignmentsGrid.canEdit = this.canEdit

        this.refresh()
    }

    ngOnDestroy() {
        this.divisionsGrid.modified.off(this.refreshHandler)
    }

    refresh() {
        const sub = this.services.data
            .load(
                true,
                true,
                this.services.data.divisions.queryAll(),
                this.services.data.divisionJobs.queryAll(),
                this.services.data.jobTypes.queryAll(),
                this.services.data.vpJobs.queryAll(),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: result => {
                    const divisions = result[0] as Division[]
                    const divisionJobs = result[1] as DivisionJob[]
                    const jobTypes = result[2] as JobType[]
                    const activeJobs = result[3] as VpJob[]

                    this.applyJobTypeSummaries(divisions, jobTypes)
                    this.divisionsGrid.items = divisions

                    this.divisionMap = new DataMap(
                        divisions.map(d => new ListItem<string>(d.id, d.name)),
                        'value',
                        'display',
                    )
                    this.divisionEditMap = new DataMap(
                        [
                            new ListItem<string>('', '(Unassigned)'),
                            ...divisions.filter(d => !d.archived).map(d => new ListItem<string>(d.id, d.name)),
                        ],
                        'value',
                        'display',
                    )

                    this.jobToDivisionJob.clear()
                    divisionJobs.forEach(divisionJob => {
                        if (!this.jobToDivisionJob.has(divisionJob.jobId)) {
                            this.jobToDivisionJob.set(divisionJob.jobId, divisionJob)
                        }
                    })

                    activeJobs.forEach(job => {
                        job['divisionId'] = this.jobToDivisionJob.get(job.id)?.divisionId ?? ''
                    })
                    this.projectAssignmentsGrid.items = activeJobs

                    this.initialLoadingComplete()
                },
            })
        this.cancelableRequest('', true, sub)
    }

    private applyJobTypeSummaries(divisions: Division[], jobTypes: JobType[]) {
        divisions.forEach(division => {
            const owned = jobTypes.filter(jt => jt.divisionId === division.id)
            division.jobTypeCount = owned.length
            division.jobTypeNames = owned.map(jt => jt.shortName).join(', ')
        })
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // While editing, restrict the dropdown to non-archived divisions plus the blank option.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'divisionId') col.dataMap = this.divisionEditMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        const col = flexGrid.columns[args.col] as Column

        // Restore the full division map so archived assignments still render after editing.
        if (col.binding == 'divisionId') col.dataMap = this.divisionMap

        if (args.cancel || col.binding != 'divisionId') return

        const job = flexGrid.rows[args.row].dataItem as VpJob
        const newDivisionId = (job['divisionId'] as string) || ''
        const existing = this.jobToDivisionJob.get(job.id)

        if (newDivisionId) {
            if (existing) {
                if (existing.divisionId !== newDivisionId) {
                    existing.divisionId = newDivisionId
                    this.services.data.divisionJobs.edit(existing).subscribe({
                        error: () => this.onSaveError(),
                    })
                }
            } else {
                const divisionJob = new DivisionJob()
                divisionJob.jobId = job.id
                divisionJob.divisionId = newDivisionId
                this.services.data.divisionJobs.add(divisionJob).subscribe({
                    next: saved => this.jobToDivisionJob.set(job.id, saved),
                    error: () => this.onSaveError(),
                })
            }
        } else if (existing) {
            this.services.data.divisionJobs.delete(existing).subscribe({
                next: () => this.jobToDivisionJob.delete(job.id),
                error: () => this.onSaveError(),
            })
        }
    }

    private onSaveError() {
        this.services.notification.error('The division assignment was not successfully saved')
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.AdminDivisionsEdit])
    }
}
